import React from "react";
import { Link } from "react-router-dom";
import { Globe2, Gamepad2, Users, ChevronRight } from "lucide-react";
import { track } from "@/lib/track";
import { useTierPresentation } from "@/lib/ageCopy";

/** Compact "Discover, Play & Learn" strip — three doors to EXISTING destinations (no new content):
 *  3D globe · Continent Battle (solo vs AI or 2-player) · the grown-ups area. Child copy playful; adult detail lives at /grown-ups. */
const CARDS = [
  { id: "country", to: "/globe", Icon: Globe2, tint: "mwq-card-tint-primary", art: "/generated/tile-explore.webp",
    title: "Discover a Country", young: "Spin the globe. Tap a country!", older: "Spin the 3D globe, tap any country — flag, capital, wonders." },
  { id: "geo-game", to: "/games/duel/continent", Icon: Gamepad2, tint: "mwq-card-tint-reward", art: "/generated/game-first-quest.png",
    title: "Play a Geography Game", young: "Flag up, continent down. Tap fast!", older: "Continent Battle — solo vs AI or two players on one device." },
  { id: "grownups", to: "/grown-ups", Icon: Users, tint: "mwq-card-tint-success", art: null, adult: true,
    title: "For Parents & Teachers", young: "Grown-ups: progress, plans and class tools.", older: "Grown-ups: progress, weekly plans, curriculum maps and class tools." },
];

export default function DiscoverPlayLearn() {
  const { tier } = useTierPresentation();
  const young = tier === "seedlings";
  return (
    <section className="mt-7 md:mt-9" data-testid="home-discover-play-learn" aria-labelledby="home-dpl-title">
      <h2 id="home-dpl-title" className="mwq-card-title text-[1.15rem] md:text-[1.35rem]">Discover, Play &amp; Learn</h2>
      <ul className="mt-3 grid gap-2.5 md:gap-3 grid-cols-1 sm:grid-cols-3" role="list">
        {CARDS.map(({ id, to, Icon, tint, art, title, adult, ...c }) => (
          <li key={id} className={adult ? "max-sm:col-span-full" : ""}>
            <Link to={to} data-testid={`home-dpl-${id}`} onClick={() => track("discovery_item_clicked", { kind: "dpl", id, to })}
              className={`mwq-card ${tint} flex items-center gap-3 h-full min-h-[88px] ${adult ? "border-dashed max-sm:min-h-[48px] max-sm:py-2 max-sm:px-3 max-sm:mt-1 max-sm:bg-transparent max-sm:shadow-none max-sm:border-x-0 max-sm:border-b-0 max-sm:rounded-none max-sm:border-t-2" : ""}`}
              style={adult ? { fontFamily: "var(--font-ui)" } : undefined}>
              {art ? (
                <img src={art} alt="" width={56} height={56} loading="lazy" decoding="async" className="h-14 w-14 shrink-0 rounded-xl object-cover border border-[var(--color-border)]" />
              ) : (
                <span className="h-14 w-14 shrink-0 rounded-xl grid place-items-center bg-white border border-[var(--color-border)] max-sm:hidden"><Icon size={26} strokeWidth={2.5} aria-hidden /></span>
              )}
              <span className="min-w-0 flex-1">
                <span className={`block font-bold leading-tight ${young ? "text-lg" : "text-base"} ${adult ? "max-sm:text-sm" : ""}`}>{title}</span>
                <span className={`mwq-meta block mt-0.5 normal-case tracking-normal line-clamp-2 ${adult ? "max-sm:hidden" : ""}`}>{young ? c.young : c.older}</span>
              </span>
              <ChevronRight size={18} strokeWidth={3} className="shrink-0 opacity-60" aria-hidden />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
